
import { defineComponent, computed, getCurrentInstance, PropType, ref, watch, onMounted } from 'vue';
import Toast from 'vant/lib/toast';
import { AIFeed } from '@/api/ai-feed';
import { AIFeedbackMessageInfo, AIMessage, AIMessageStatus, AIMessageType } from '~/store/modules/ai-conversation';
import { checkIfBomdiaApp, checkIfKwaiProApp } from '@/common/app';
import AudioPlayTextComponent from '../../ai-audio-player/audio-text.vue';
import AgreeIcon from '../../../images/agree.png';
import AgreedIcon from '../../../images/agreed.png';
import DisagreeIcon from '../../../images/disagree.png';
import DisagreedIcon from '../../../images/disagreed.png';
import { sendLog } from '@/common/radar';
import { isChineseComplianceWhiteListUser } from '~/common/white-list-user';
import MessageTextMD from './message-text-md.vue';

const AIChatMessageText = defineComponent({
    components: {
        AudioPlayTextComponent,
        MessageTextMD,
    },
    props: {
        aiFeed: {
            type: Object as PropType<AIFeed> | undefined,
            default: undefined,
        },
        message: {
            type: Object as PropType<AIMessage> | undefined,
            default: undefined,
        },
        messageList: {
            type: Array as PropType<AIMessage[]> | undefined,
            default: () => [],
        },
        messageIndex: {
            type: Number,
            default: 0,
        },
        history: {
            type: Boolean,
            default: false,
        },
        last: {
            type: Boolean,
            default: false,
        },
        curBotIndex: {
            type: Number,
            default: 0,
        },
        botIndex: {
            type: Number,
            default: 0,
        },
    },

    setup(props) {
        const isWhiteListUser = ref<boolean>(true);
        const { proxy } = getCurrentInstance() || {};

        const itemMsgTextBotId = ref(props?.message?.botId);

        const curBotIndexNumber = computed(() => {
            return props?.curBotIndex;
        });

        const messageIndexNumber = computed(() => {
            return props?.messageIndex;
            // return props?.aiFeed?.index;
        });

        const messageIdStr = computed(() => {
            return props?.message?.messageId;
        });

        const feed = computed(() => {
            return props?.aiFeed;
        });

        // const loadingAIResponse = ref(true);
        const loadingAIResponse = computed(() => {
            // eslint-disable-next-line no-use-before-define
            return isOther.value && props.message?.messageStatus === AIMessageStatus.loading;
        });
        const isOther = computed(() => {
            return props?.message?.messageType !== AIMessageType.me;
        });

        const showMessageAction = computed(() => {
            return isOther.value && props?.message?.messageStatus === AIMessageStatus.done && !checkIfBomdiaApp() && !checkIfKwaiProApp();
        });

        const markdownTest = computed(() => {
            return proxy?.$route?.query?.markdownTest === 'true';
        });

        const showMessageFeedbackAction = computed(() => {
            const { ai_message_feedback_action = false } = proxy?.$store?.state?.abTestModule?.abTestParams || {};

            return (
                ai_message_feedback_action &&
                props.last &&
                isOther.value &&
                props?.message?.messageStatus === AIMessageStatus.done &&
                !checkIfBomdiaApp()
            );
        });

        const feedbackType = computed(() => {
            const feedbackMessageInfo = ((proxy?.$store?.state?.conversationModule?.feedbackMessageInfoMap || {})[props.message?.botId || ''] ||
                {}) as AIFeedbackMessageInfo;

            /**
             * @zh 产生新的对话后，store 里保存的 message 信息还未更新，需要做一层判断
             */
            if (feedbackMessageInfo?.messageId !== props?.message?.messageId) {
                return undefined;
            }

            return feedbackMessageInfo?.type;
        });

        // const isMe = computed(() => {
        //     return props?.message?.messageType === AIMessageType.me;
        // });
        // const canShow = computed(() => {
        //     if (!props?.history) return true;
        //     if (isMe.value) return true;
        //     if (props?.message?.messageStatus !== AIMessageStatus.loading) return true;
        //     return !props.last;
        // });
        // Me
        const chatMeText = computed(() => {
            if (!props?.message || props?.message?.messageType !== AIMessageType.me) {
                return ''; // 如果条件不满足，返回空字符串
            }
            return props.message.content || ''; // 确保返回的是字符串
        });
        // Other
        const chatOtherText = computed(() => {
            if (!props?.message || props?.message?.messageType === AIMessageType.me) return '';
            return props?.message.content;
        });

        const chatOtherTextArray = computed<string[]>(() => {
            // eslint-disable-next-line no-use-before-define
            const result = extractAndMergeContent(chatOtherText.value || '');
            return result || [];
        });

        const chatMeTextArray = computed<string[]>(() => {
            // eslint-disable-next-line no-use-before-define
            const result = extractAndMergeContentForMe(chatMeText.value || '');
            return result || [];
        });

        // Tools
        const isNarrator = (text: string) => {
            return /^\([^\\)]*\)$/.test(text);
        };

        const NEWLINE_MARKER = '__NEWLINE__';
        const extractAndMergeContent = (str: string): string[] => {
            if (!str) return [];

            // 先替换掉 {{user}} 的内容及其后面的逗号
            str = str.replace(/\{\{user\}\},?/g, '');

            // 替换 {{char}} 为 'bot name'
            str = str.replace(/\{\{char\}\}/g, props?.aiFeed?.bot_name || '');

            // 分步骤处理换行符
            str = str
                // 处理双反斜杠换行
                .replace(/\\\\n/g, NEWLINE_MARKER)
                // 处理连续两个换行
                .replace(/\n\n/g, NEWLINE_MARKER)
                // 处理单个换行
                .replace(/\\n/g, NEWLINE_MARKER)
                // 处理剩余的换行
                .replace(/\n/g, NEWLINE_MARKER);

            const result: string[] = [];

            // 先按换行符分割
            const segments = str.split(NEWLINE_MARKER);

            segments.forEach((segment, index) => {
                if (segment.trim()) {
                    // 处理每个非空段落中的其他格式
                    const regex = /"(.*?\*.*?\*.*?)"|\*([^*]*)\*|\\'([^']*)\\'|\\"([^"]*)\\"|\s+|([^*"'\\]+)/g;

                    const matches = segment.match(regex);
                    if (matches) {
                        matches.forEach(match => {
                            // 处理双引号中的星号内容
                            if (match.startsWith('"') && match.endsWith('"')) {
                                const asteriskMatch = match.match(/\*(.*?)\*/);
                                if (asteriskMatch) {
                                    const parts = match.split(/\*(.*?)\*/);
                                    parts.forEach((part, i) => {
                                        if (i % 2 === 0) {
                                            // 非星号部分，去掉首尾引号
                                            const cleanPart = part.replace(/^"|"$/g, '').trim();
                                            if (cleanPart) result.push(cleanPart);
                                        } else {
                                            // 星号部分
                                            result.push(`(${part})`);
                                        }
                                    });
                                } else {
                                    // 没有星号的双引号内容
                                    result.push(match.slice(1, -1));
                                }
                            } else if (match.startsWith('*') && match.endsWith('*')) {
                                // 处理星号内容
                                result.push(`(${match.slice(1, -1)})`);
                            } else if (match.startsWith("\\'") && match.endsWith("\\'")) {
                                // 处理转义单引号内容
                                result.push(match.slice(2, -2));
                            } else if (match.startsWith('\\"') && match.endsWith('\\"')) {
                                // 处理转义双引号内容
                                result.push(match.slice(2, -2));
                            } else if (match.trim()) {
                                // 处理普通文本
                                result.push(match.trim());
                            }
                        });
                    }
                }

                // 在非最后一个段落后添加换行标记
                if (index < segments.length - 1) {
                    result.push(NEWLINE_MARKER);
                }
            });

            // 清理结果数组
            return result.filter(item => item && item.trim());
        };
        const extractAndMergeContentForMe = (str: string): string[] => {
            if (!str) return [];

            // 正则表达式，匹配 (内容) 或其他内容
            const regex = /\((.*?)\)|([^()]+)/g;

            const result: string[] = [];

            // 使用 replace 方法遍历匹配结果
            str.replace(regex, (match, group1, group2) => {
                if (group1) {
                    // 如果匹配到 (内容)，将其添加到结果数组中
                    result.push(`(${group1})`);
                } else if (group2) {
                    // 如果匹配到其他内容，直接添加到结果数组中
                    result.push(group2);
                }
                return match; // 返回匹配的内容（不需要使用）
            });

            return result;
        };
        const scrollToBottom = () => {
            // eslint-disable-next-line no-use-before-define
            const scrollHeight = chatOtherRef?.value?.scrollHeight;
            if (scrollHeight > 0) {
                // eslint-disable-next-line no-use-before-define
                chatOtherRef.value.scrollTo(0, scrollHeight);
            }
        };
        // gradually show answer, not used anymore
        const changeOtherToParagraphs = (text: string) => {
            const maxCharsPerParagraph = 20;
            const paragraphs = [];
            let currentParagraph = '';
            for (let i = 0; i < text.length; i++) {
                if (currentParagraph.length >= maxCharsPerParagraph) {
                    paragraphs.push(currentParagraph);
                    currentParagraph = '';
                } else if (paragraphs.length > 20) {
                    break;
                }
                currentParagraph += text[i];
            }
            if (currentParagraph) {
                paragraphs.push(currentParagraph);
            }
            return paragraphs;
        };
        const loadParagraphs = (paragraphs: string[]) => {
            const localParas = paragraphs;
            const interval = setInterval(() => {
                if (localParas.length > 0) {
                    // chatOtherShowedText.value += localParas.shift();
                } else {
                    clearInterval(interval);
                }
            }, 100);
        };

        // 处理数组的方法，去除被 () 包住的部分
        const processMsgTextArray = (arr: string[]): string => {
            return arr
                .filter(item => !/^\(.*\)$/.test(item)) // 先过滤掉整个被括号包住的元素
                .map(item => item.replace(/\(.*?\)/g, '')) // 再去除每个元素内被括号包住的部分
                .filter(item => item.trim() !== '') // 过滤掉空字符串
                .join(' '); // 用空格拼接剩余的字符串
        };

        // 计算处理后的字符串
        const itemMsgTextContent = computed(() => {
            const result = processMsgTextArray(chatOtherTextArray.value);
            return result;
        });

        const showMessageAudioButton = computed(() => {
            const result =
                isOther.value &&
                props?.message?.messageStatus === AIMessageStatus.done &&
                props?.aiFeed?.showTone &&
                itemMsgTextContent?.value !== '';
            return result;
        });

        /* Events */
        let startY: number;
        let moveY;
        let currentScrollTop: number;
        const chatOtherRef = ref();
        const handleChatOtherTouchStart = (e: TouchEvent) => {
            startY = e.touches[0].clientY;
            currentScrollTop = chatOtherRef.value.scrollTop;
        };
        const handleChatOtherTouchMove = (e: TouchEvent) => {
            const { scrollTop, scrollHeight, clientHeight } = chatOtherRef.value;
            const touch = e.touches[0];
            const rect = chatOtherRef.value.getBoundingClientRect();
            const x = touch.clientX - rect.left;
            const y = touch.clientY - rect.top;
            if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) {
                e.stopPropagation();
                return;
            }
            moveY = e.touches[0].clientY;
            const diffY = startY - moveY;
            const toScrollTop = currentScrollTop + diffY;
            chatOtherRef.value.scrollTop = toScrollTop;
            if (scrollTop === 0) {
                if (currentScrollTop !== 0) {
                    e.stopPropagation();
                }
                return;
            }
            if (scrollTop + clientHeight >= scrollHeight) {
                if (currentScrollTop + clientHeight < scrollHeight) {
                    e.stopPropagation();
                }
                return;
            }
            e.stopPropagation();
        };
        const handleChatOtherTouchEnd = (e: TouchEvent) => {
            // startY = 0;
            // moveY = 0;
            // e.stopPropagation();
        };

        const handleSelectMessage = (e: Event) => {
            e.stopPropagation();
            proxy?.$store?.commit?.('conversationModule/setSelectedMessageInfo', {
                ...(props.message || {}),
                messageIndex: Number(props.messageIndex) + 1,
            });
            sendLog({
                type: 'click',
                name: 'MESSAGE_FEEDBACK_BUTTON',
                value: {
                    bot_id: props.aiFeed?.bot_id_str || '',
                    message_index: Number(props.messageIndex) + 1,
                    message: props.message?.content || '',
                },
            });
        };

        const handleFeedbackAgree = (e: Event) => {
            e.stopPropagation();
            proxy?.$store?.commit?.('conversationModule/setFeedbackMessageInfo', {
                [props.aiFeed?.bot_id_str || '']: {
                    ...(props.message || {}),
                    type: 'agree',
                    showFeedbackPopup: false,
                    messageIndex: Number(props?.messageIndex || 0),
                },
            });
            // 上报埋点、多语言
            Toast({
                message: proxy?.$root?.$t?.('k_1323882'),
            });
            sendLog({
                type: 'click',
                name: 'MESSAGE_FEEDBACK_POPUP',
                value: {
                    click_area: 'like',
                    bot_id: props?.aiFeed?.bot_id_str || '',
                    message_index: Number(props?.messageIndex || 0) + 1,
                },
            });
        };

        const handleFeedbackDisagree = (e: Event) => {
            // 上报埋点
            e.stopPropagation();
            proxy?.$store?.commit?.('conversationModule/setFeedbackMessageInfo', {
                [props.aiFeed?.bot_id_str || '']: {
                    ...(props.message || {}),
                    type: 'disagree',
                    showFeedbackPopup: true,
                    messageIndex: Number(props?.messageIndex || 0),
                },
            });
        };

        const handleShowRedExclamation = (text: string) => {
            // 屏蔽中文输入
            const regex = /[\u4e00-\u9fa5]/;
            return regex.test(text);
        };

        watch(
            () => chatOtherText.value,
            newVal => {
                if (newVal.length > 0) {
                    scrollToBottom();
                }
            }
        );

        onMounted(async () => {
            // 合规白名单用户不屏蔽中文输入
            isWhiteListUser.value = await isChineseComplianceWhiteListUser();
        });

        return {
            feed,
            isOther,
            isNarrator,
            chatMeText,
            chatOtherText,
            chatOtherTextArray,
            handleChatOtherTouchStart,
            handleChatOtherTouchMove,
            handleChatOtherTouchEnd,
            scrollToBottom,
            chatOtherRef,
            loadingAIResponse,
            showMessageAction,
            showMessageFeedbackAction,
            feedbackType,
            chatMeTextArray,
            showMessageAudioButton,
            handleSelectMessage,
            itemMsgTextBotId,
            itemMsgTextContent,
            curBotIndexNumber,
            messageIndexNumber,
            messageIdStr,
            isWhiteListUser,

            AgreeIcon,
            AgreedIcon,
            DisagreeIcon,
            DisagreedIcon,

            handleFeedbackDisagree,
            handleFeedbackAgree,
            handleShowRedExclamation,
            NEWLINE_MARKER,
            markdownTest,
        };
    },
});

export default AIChatMessageText;
